import { Link } from 'react-router-dom';
import { ArrowRight, Check, PackageOpen, ShieldCheck, UserMinus, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, cn } from '@mlm/ui';
import type { KpiCardData } from '../../lib/api';
import { TenantWideBadge } from './tenant-wide-badge';

/**
 * The queues that need an operator to act: open orders, payout requests and
 * KYC submissions, plus accounts the inactivity rule has blocked (only while
 * that rule is on). This is the admin's "one next step" surface, so each row is
 * a link to where the work gets done. A non-empty queue reads ochre (pending);
 * an empty one stays calm (no manufactured urgency).
 */
type Queue = {
  data?: Pick<KpiCardData, 'value' | 'scope'>;
  singular: string;
  plural: string;
  suffix: string;
  to: string;
  Icon: typeof Wallet;
};

export function AttentionStrip({
  orders,
  payouts,
  kyc,
  inactivityBlocked,
  filtered = false,
  branchId,
}: {
  orders?: KpiCardData;
  payouts?: KpiCardData;
  kyc?: KpiCardData;
  /** Omitted when the inactivity rule is off, which hides the row. */
  inactivityBlocked?: { count: number };
  filtered?: boolean;
  branchId?: string;
}) {
  const queues: Queue[] = [
    {
      data: orders,
      singular: 'order',
      plural: 'orders',
      suffix: 'awaiting action',
      // source=all: the KPI counts house AND merchant sub-orders, so the
      // clicked-through list must too (the Orders page defaults to inhouse).
      // The branch rides along for the same reason: the count is filtered on
      // `orders.branch_id`, and so is the list it lands on — without this,
      // "14 orders awaiting action" opens a list of a different number.
      to:
        '/orders?status=pending,to_pay,paid,to_pick,to_ship,to_release&source=all' +
        (branchId ? `&branchId=${branchId}` : ''),
      Icon: PackageOpen,
    },
    {
      data: payouts,
      singular: 'payout request',
      plural: 'payout requests',
      suffix: 'waiting',
      to: '/payouts',
      Icon: Wallet,
    },
    {
      data: kyc,
      singular: 'KYC submission',
      plural: 'KYC submissions',
      suffix: 'to verify',
      to: '/kyc-review',
      Icon: ShieldCheck,
    },
    {
      data: inactivityBlocked
        ? { value: String(inactivityBlocked.count), scope: 'tenant' }
        : undefined,
      singular: 'account',
      plural: 'accounts',
      suffix: 'blocked for inactivity',
      to: '/members?status=suspended&blocked=inactivity',
      Icon: UserMinus,
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-h3">Needs attention</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y">
          {/* No data (old API / failed fetch) → hide the row rather than
              render a false-calm "No … awaiting" claim. */}
          {queues
            .filter((q) => q.data !== undefined)
            .map((q) => (
              <Row key={q.to + q.singular} queue={q} filtered={filtered} />
            ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function Row({ queue, filtered }: { queue: Queue; filtered: boolean }) {
  const n = Number(queue.data?.value ?? 'NaN');
  const pending = !Number.isNaN(n) && n > 0;
  const { Icon } = queue;
  // Driven off the KPI's own declared scope, never the row's position — if a
  // queue ever gains a branch dimension server-side, the badge goes away by
  // itself.
  const tenantWide = filtered && queue.data?.scope === 'tenant';
  return (
    <li>
      <Link
        to={queue.to}
        className="group flex items-center gap-3 px-6 py-3 transition-colors hover:bg-muted"
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
            pending ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground',
          )}
        >
          {pending ? <Icon className="h-4 w-4" /> : <Check className="h-4 w-4" />}
        </span>
        <p className="flex min-w-0 flex-1 items-center gap-2 text-body">
          <span>
            {pending ? (
              <>
                <span className="font-semibold tabular-nums text-warning">{n}</span>{' '}
                {n === 1 ? queue.singular : queue.plural} {queue.suffix}
              </>
            ) : (
              <span className="text-muted-foreground">
                No {queue.plural} {queue.suffix}
              </span>
            )}
          </span>
          <TenantWideBadge show={tenantWide} />
        </p>
        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 text-caption font-medium',
            pending ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          {pending ? 'Review' : 'View'}
          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Link>
    </li>
  );
}
